import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DependentsInformativeModal from './sections/DependentsInformativeModal/DependentsInformativeModal';
import AddDependentsByFileTable from './sections/TableWithErrors/AddDependentsByFileTable';
import { mapDependentsByFileDataToDependentsByFileToUpload } from './utils/mapper';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsCardContainer from '../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import FileDropzone from '../../../../../shared/components/v3/FileDropzone/FileDropzone';
import BdsDivider from '../../../../../shared/components/v3/Globals/BdsDivider/BdsDivider';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { COLLABORATORS_HOME_ROUTE } from '../../../../../shared/constants/auth';
import { DOWNLOAD_BLACK_ICON } from '../../../../../shared/constants/images';
import { SUBSCRIPTION_FILE_UPLOADER_VALIDATORS } from '../../../../../shared/constants/manageCollaborators';
import {
  CLICK_ACTION,
  ERROR_ACTION,
  FAMILY_FILE,
  FILE_FORMAT_ERROR,
  FILE_SIZE_ERROR,
  GENERIC_ERROR,
  INVITE_FAMILY_CONFIRM_NEW_RECORDS,
  TABLE_ERROR
} from '../../../../../shared/constants/tagManager';
import {
  ADD_DEPENDENTS_FORMAT_ERROR_TOAST,
  ADD_DEPENDENTS_LIMIT_ERROR_TOAST,
  DOWNLOAD_TEMPLATE_ERROR_TOAST,
  DOWNLOAD_TEMPLATE_SUCCESS_TOAST,
  GENERIC_ERROR_TOAST
} from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import type {
  FileState,
  ToastState,
  UIState,
  IAddDependentsByFile,
  IRequestAddDependentsFile,
  IDependentsByFileToUpload,
  IValidateDependentsFileError,
  IValidateDependentsFileSuccess,
  IDependentsWithErrors
} from '../../../../../shared/interfaces/manageCollaborators';
import type { IToast } from '../../../../../shared/interfaces/toast';
import Companies from '../../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../shared/translations/manageCollaborators';
import { downloadFile, toLocalStorage } from '../../../../../shared/utils/utils';

import './AddDependentsByFile.scss';

const AddDependentsByFile = ({ setProgress }: IAddDependentsByFile) => {
  const [fileState, setFileState] = useState<FileState>({
    base64: '',
    isValid: false,
    showCorrectedMessage: false
  });
  const [uiState, setUiState] = useState<UIState>({ loading: false, resetDropzone: false });
  const [toast, setToast] = useState<ToastState>({ open: false, data: undefined });
  const [showTableErrors, setShowTableErrors] = useState<boolean>(false);
  const [dependents, setDependents] = useState<IDependentsByFileToUpload[]>([]);
  const [validDependents, setValidDependents] = useState<IDependentsByFileToUpload[]>([]);
  const [dependentsWithError, setDependentsWithError] = useState<IValidateDependentsFileError>();
  const [flowState, setFlowState] = useState({ failed: false, success: false });
  const [dataToUpload, setDataToUpload] = useState<IValidateDependentsFileSuccess[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFormatError, setIsFormatError] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const { company } = useContext(CompanyContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const disabledButton = useMemo(() => {
    if (flowState.failed) {
      return !fileState.isValid || uiState.loading;
    } else {
      return !fileState.isValid || uiState.loading || !flowState.success;
    }
  }, [fileState.isValid, flowState.failed, flowState.success, uiState.loading]);

  const showToast = useCallback((toastConfig: IToast) => {
    setToast({
      open: true,
      data: toastConfig
    });
  }, []);

  const showLimitRowsToast = useCallback(() => {
    showToast(ADD_DEPENDENTS_LIMIT_ERROR_TOAST);
    setFileState((prev) => ({
      ...prev,
      isValid: false,
      showCorrectedMessage: false
    }));
    setUiState((prev) => ({
      ...prev,
      resetDropzone: !prev.resetDropzone
    }));
  }, [showToast]);

  const showFormatErrorToast = useCallback(() => {
    showToast(ADD_DEPENDENTS_FORMAT_ERROR_TOAST);
    setFileState((prev) => ({
      ...prev,
      isValid: false,
      showCorrectedMessage: false
    }));
    setUiState((prev) => ({
      ...prev,
      resetDropzone: !prev.resetDropzone
    }));
    setIsFormatError(true);
  }, [showToast]);

  const sendDependents = async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      await Companies.addDependentsByFile(company.id, requestId);

      const totalDependents = dataToUpload.reduce((acc, curr) => acc + curr.dependants.length, 0);
      const json = {
        openModal: true,
        amount: totalDependents
      };

      const localData = JSON.stringify(json);
      toLocalStorage('dependentsAdded', localData);

      navigate(COLLABORATORS_HOME_ROUTE);
    } catch {
      showToast(GENERIC_ERROR_TOAST);
    } finally {
      setLoading(false);
    }
  };

  const validateFile = useCallback(
    async (args: IRequestAddDependentsFile, sendData?: boolean) => {
      if (!args) return;

      setUiState((prev) => ({ ...prev, loading: true }));
      setDependentsWithError(undefined);
      setDependents([]);
      setValidDependents([]);
      setIsFormatError(false);
      try {
        const response = await Companies.validateDependentsFile({
          companyId: company.id,
          countryCode: company.countryCode,
          records: args.dependents,
          base64: args.fileBase64
        });

        if (response.success) {
          const result = response.data as IValidateDependentsFileSuccess[];
          setFileState((prev) => ({
            ...prev,
            isValid: true,
            showCorrectedMessage: true
          }));
          setFlowState((prev) => ({ ...prev, success: true }));
          setDataToUpload(result);
          setRequestId(response.requestId);
          setProgress(100);
          if (sendData) {
            TagManagerService.pushEvent(CLICK_ACTION, INVITE_FAMILY_CONFIRM_NEW_RECORDS);
            sendDependents();
          }
        } else {
          const result = response.data as IValidateDependentsFileError;
          const dependentsValids = result.valids;
          setShowTableErrors(true);
          setDependentsWithError(result);
          setValidDependents(dependentsValids);
          TagManagerService.pushEvent(ERROR_ACTION, FAMILY_FILE, TABLE_ERROR);
        }
      } catch (error: any) {
        if (error?.response?.data?.code === 54) {
          TagManagerService.pushEvent(ERROR_ACTION, FAMILY_FILE, FILE_SIZE_ERROR);
          showLimitRowsToast();
        } else if (error?.response?.data?.code === 122) {
          TagManagerService.pushEvent(ERROR_ACTION, FAMILY_FILE, FILE_FORMAT_ERROR);
          showFormatErrorToast();
        } else {
          TagManagerService.pushEvent(ERROR_ACTION, FAMILY_FILE, GENERIC_ERROR);
          showToast(GENERIC_ERROR_TOAST);
          setUiState((prev) => ({
            ...prev,
            resetDropzone: !prev.resetDropzone
          }));
        }
        setFileState((prev) => ({ ...prev, isValid: false }));
        setFlowState((prev) => ({ ...prev, success: false }));
      } finally {
        setUiState((prev) => ({ ...prev, loading: false }));
      }
    },
    [company, showLimitRowsToast, showToast, setProgress, showFormatErrorToast]
  );

  const handleTemplateDownload = useCallback(async () => {
    try {
      const response = await Companies.downloadDependentsTemplate(company.id, company.countryCode);

      if (response?.url) {
        downloadFile(response.url);
        showToast(DOWNLOAD_TEMPLATE_SUCCESS_TOAST);
      }
    } catch {
      showToast(DOWNLOAD_TEMPLATE_ERROR_TOAST);
    }
  }, [company, showToast]);

  const handleFileUpdate = useCallback((base64File: string) => {
    if (base64File) {
      setFileState((prev) => ({ ...prev, isValid: true, base64: base64File }));
    } else {
      setFileState((prev) => ({
        ...prev,
        base64: '',
        error: '',
        isValid: false,
        showCorrectedMessage: false
      }));
    }
  }, []);

  const handleCloseToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
    setIsFormatError(false);
  }, []);

  const handleSetDependentToUpload = (dependents: IDependentsWithErrors[]) => {
    const dependentsToUpload = dependents.map(mapDependentsByFileDataToDependentsByFileToUpload);
    setDependents(dependentsToUpload);
  };

  const dependentsCounter = useCallback(() => {
    if (showTableErrors && !dataToUpload.length) {
      return dependents.length + validDependents.length;
    }
    const totalDependents = dataToUpload.reduce((acc, curr) => acc + curr.dependants.length, 0);
    return totalDependents;
  }, [dependents, validDependents, showTableErrors, dataToUpload]);

  useEffect(() => {
    if (fileState.base64) {
      validateFile({ fileBase64: fileState.base64 });
    }
  }, [fileState.base64, validateFile]);

  const handleNextStep = () => {
    if (flowState.failed && !dataToUpload.length) {
      validateFile({ dependents: [...validDependents, ...dependents] }, true);
    } else {
      TagManagerService.pushEvent(CLICK_ACTION, INVITE_FAMILY_CONFIRM_NEW_RECORDS);
      sendDependents();
    }
  };

  const renderSkeletons = () => {
    return (
      <div className="skeletons flex flex-row justify-between w-full">
        <Skeleton className="skeleton" animation={false} />
      </div>
    );
  };

  return (
    <>
      <div className="add-dependents-by-file flex flex-col w-full" data-experience="new">
        <div className="flex flex-row justify-between items-center">
          <h3 className="template-title">
            {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.title)}
          </h3>
          <DependentsInformativeModal countryCode={company.countryCode.toUpperCase()} />
        </div>
        <BdsCardContainer paddingSize="padding-l" borderRadiusSize="radius-l" className="text-area-container">
          <div className="top-information flex flex-col w-full">
            <div className="flex flex-row align-center justify-between">
              {uiState.loading ? (
                renderSkeletons()
              ) : (
                <span>
                  {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.addDescription, {
                    count: dependentsCounter()
                  })}
                </span>
              )}
            </div>
            <div className="flex flex-row align-center justify-between">
              {uiState.loading ? (
                renderSkeletons()
              ) : (
                <h3>
                  {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.numberOfFamily, {
                    total: dependentsCounter(),
                    count: dependentsCounter()
                  })}
                </h3>
              )}
            </div>
          </div>
          <BdsDivider size="s" color="subtle" />
          {showTableErrors ? (
            <AddDependentsByFileTable
              dependentsWithError={dependentsWithError?.invalids ?? []}
              setDependentToUpload={handleSetDependentToUpload}
              setReadyToUpload={(ready) => setFlowState((prev) => ({ ...prev, failed: ready }))}
            />
          ) : (
            <div className="flex flex-row w-full mt-10">
              <div className="add-dependents-template flex flex-column items-start gap-6">
                <div className="add-dependents-template-title">
                  {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.downloadTemplate)}
                </div>
                <BdsButton action={handleTemplateDownload} variant="secondary" size="m" iconLeft={DOWNLOAD_BLACK_ICON}>
                  {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.downloadTemplateButton)}
                </BdsButton>
              </div>
              <div className="add-dependents-file-dropzone">
                <FileDropzone
                  updateBase64={handleFileUpdate}
                  reset={uiState.resetDropzone}
                  loading={uiState.loading}
                  validator={SUBSCRIPTION_FILE_UPLOADER_VALIDATORS}
                  inputTitle={t(TRANSLATIONS_GLOBAL.fileDropzone.fileTitle)}
                  correctedFileMessage={fileState.showCorrectedMessage}
                />
              </div>
            </div>
          )}
        </BdsCardContainer>
        <div className="manage-collaborators-button">
          <BdsButton variant="primary" size="l" action={handleNextStep} loading={loading} disabled={disabledButton}>
            {t(TRANSLATION_MANAGE_COLLABORATORS.addDependentsCard.addDependentsByFile.addDependentsButton)}
          </BdsButton>
        </div>
      </div>
      <Toast
        title={t(toast.data?.title || '')}
        description={t(toast.data?.description || '')}
        icon={toast.data?.icon || 'error'}
        open={toast.open}
        button={toast.data?.button}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
        secondaryActionData={
          isFormatError
            ? {
                text: t(TRANSLATIONS_GLOBAL.toasts.addDependentsFormatError.button),
                action: handleTemplateDownload
              }
            : undefined
        }
      />
    </>
  );
};

export default AddDependentsByFile;
